import { Routes, Route, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { 
  ArrowRight, 
  Terminal, 
  CheckCircle2,
  XCircle
} from 'lucide-react'
import { useState, useEffect } from 'react'
import { PrivacyPolicy } from './pages/Privacy'
import { RefundPolicy } from './pages/Refund'
import { TermsOfService } from './pages/Terms'
import { Waitlist } from './pages/Waitlist'
import { LoginPage } from './pages/auth/Login'
import { SignupPage } from './pages/auth/Signup'
import { ProtectedRoute } from './components/ProtectedRoute'
import { DashboardLayout } from './components/DashboardLayout'
import { DashboardOverview } from './pages/dashboard/Overview'
import { ApiKeysPage } from './pages/dashboard/ApiKeys'
import { UsagePage } from './pages/dashboard/Usage'
import { BillingPage } from './pages/dashboard/Billing'
import { SettingsPage } from './pages/dashboard/Settings'

// Animated Terminal Component - DARK ONLY
function AnimatedTerminal() {
  const services = [
    { name: 'Search', endpoint: 'v1/search/query', desc: 'Full-text search across your data' },
    { name: 'Sandboxes', endpoint: 'v1/sandboxes/create', desc: 'Isolated execution environments' },
    { name: 'Email', endpoint: 'v1/email/send', desc: 'Send transactional emails' },
    { name: 'Storage', endpoint: 'v1/storage/upload', desc: 'Store and retrieve files' },
    { name: 'Inference', endpoint: 'v1/inference/generate', desc: 'Run AI models' },
  ]
  
  const [currentIndex, setCurrentIndex] = useState(0)
  const [displayedText, setDisplayedText] = useState('')
  
  useEffect(() => {
    const current = services[currentIndex]
    const fullCommand = `curl api.twenty.sh/${current.endpoint} -H "Authorization: $TWENTY_KEY"`
    
    setDisplayedText('')
    let charIndex = 0
    
    const typeInterval = setInterval(() => {
      if (charIndex < fullCommand.length) {
        setDisplayedText(fullCommand.slice(0, charIndex + 1))
        charIndex++
      } else {
        clearInterval(typeInterval)
        setTimeout(() => {
          setCurrentIndex((prev) => (prev + 1) % services.length)
        }, 2000)
      }
    }, 50)
    
    return () => clearInterval(typeInterval)
  }, [currentIndex])
  
  const current = services[currentIndex]
  
  return (
    <div className="bg-slate-950 rounded-lg p-6 max-w-2xl mx-auto border border-slate-800 shadow-2xl">
      <div className="flex items-center gap-2 mb-4 text-slate-400 text-sm border-b border-slate-800 pb-3">
        <Terminal className="w-4 h-4 text-red-600" />
        <span className="font-mono">api.twenty.sh</span>
      </div>
      
      <div className="space-y-3 font-mono text-sm">
        <div className="text-slate-500">
          <span className="text-red-600">#</span> {current.name} — {current.desc}
        </div>
        
        <div className="flex items-baseline text-white text-left w-full">
          <span className="text-red-600 mr-2 flex-shrink-0">$</span>
          <span className="flex-1 whitespace-pre">{displayedText}</span>
          <span className="inline-block w-2 h-4 bg-red-600 ml-1 animate-pulse flex-shrink-0" />
        </div>
      </div>
    </div>
  )
}

const problems = [
  { title: 'Integration Fatigue', desc: '5 different APIs, auth flows, rate limits, and dashboards' },
  { title: 'Security Anxiety', desc: 'Secrets management, key rotation, leak paranoia' },
  { title: 'Integration Nightmare', desc: 'Different SDKs, error formats, retry logic' },
  { title: 'The Time Trap', desc: 'Weekend project? Week 6 and still wiring infra' },
]

const solutionFeatures = [
  { title: 'One API', desc: 'Search, Sandboxes, Email, Storage, Inference. All behind a single key.' },
  { title: 'Agent-First Design', desc: 'LLMs parse our JSON without breaking. Clean errors, consistent structure.' },
  { title: 'When You Need Control', desc: 'Full access for developers who want to dig deeper. Not locked behind abstractions.' },
]

function LandingPage() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [consent, setConsent] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!consent) {
      setError('Please consent to receive beta access emails')
      return
    }
    
    setLoading(true)
    setError('')
    
    try {
      const response = await fetch('/api/beta/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email,
          consent: true,
          timestamp: new Date().toISOString(),
          source: 'landing-page'
        })
      })
      
      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.message || 'Failed to join beta list')
      }
      
      setSubmitted(true)
      setEmail('')
      setConsent(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Twenty</title>
        <meta name="description" content="Building with AI is easy. Shipping is hard. Twenty simplifies the last 20% of infrastructure so you can actually ship." />
        <meta property="og:title" content="Twenty" />
        <meta property="og:description" content="Building with AI is easy. Shipping is hard. Twenty simplifies the last 20% of infrastructure so you can actually ship." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://twenty.sh/" />
        <meta property="og:image" content="https://twenty.sh/og-image.svg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Twenty" />
        <meta name="twitter:description" content="Building with AI is easy. Shipping is hard. Twenty simplifies the last 20% of infrastructure so you can actually ship." />
        <meta name="twitter:image" content="https://twenty.sh/og-image.svg" />
        <link rel="canonical" href="https://twenty.sh/" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        {/* Nav */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
          <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
            <span className="text-xl font-semibold tracking-tight">Twenty</span>
            <div className="flex items-center gap-6">
              <a href="#problem" className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:block">The Problem</a>
              <a href="#solution" className="text-sm text-muted-foreground hover:text-foreground transition-colors hidden sm:block">How It Works</a>
              <a href="#cta" className="btn-factory text-sm">
                Join Beta
              </a>
            </div>
          </div>
        </nav>

        {/* Hero - The Hook */}
        <section className="pt-32 pb-16 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl sm:text-6xl font-semibold tracking-tight mb-6">
              Building AI apps is 
              <span className="text-primary"> easy.</span>
              <br />
              Shipping them is 
              <span className="text-slate-400"> hard.</span>
            </h1>
            
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
              You built the AI demo in a weekend. Now it is week 3 and you are 
              still wiring up infrastructure.
            </p>

            <div className="flex items-center justify-center gap-4 mb-16">
              <a href="#cta" className="btn-factory text-base px-6 py-3">
                Join the Beta
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Animated Terminal - DARK */}
            <div className="max-w-3xl mx-auto">
              <AnimatedTerminal />
            </div>
          </div>
        </section>

        {/* The Problem - Where Projects Die */}
        <section id="problem" className="py-20 px-6 bg-secondary/30 border-y border-border">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
                Why 90% of AI Projects Never Ship
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {problems.map((problem) => (
                <div key={problem.title} className="card-factory p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-red-50 rounded-sm flex items-center justify-center flex-shrink-0">
                      <XCircle className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{problem.title}</h3>
                      <p className="text-sm text-muted-foreground">{problem.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The Solution */}
        <section id="solution" className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
                It Should Not Be This Hard
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                One API key. Five services. No configuration nightmares.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {solutionFeatures.map((feature) => (
                <div key={feature.title} className="card-factory p-6 group hover:border-primary/50 transition-colors">
                  <div className="w-12 h-12 bg-green-50 rounded-sm flex items-center justify-center mb-4 group-hover:bg-primary/10 transition-colors">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The Promise */}
        <section className="py-20 px-6 bg-secondary/30 border-y border-border">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
              Ship This Weekend
            </h2>
            <p className="text-lg text-muted-foreground mb-12">
              Stop letting infrastructure kill your momentum.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
              <div className="text-center">
                <p className="text-6xl font-bold text-primary mb-2">One</p>
                <p className="text-sm text-muted-foreground">API Key</p>
              </div>
              <div className="text-4xl text-muted-foreground hidden sm:block">→</div>
              <div className="text-center">
                <p className="text-6xl font-bold text-primary mb-2">Ship</p>
                <p className="text-sm text-muted-foreground">Today</p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="py-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-semibold tracking-tight mb-4">$99/mo</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Fair use pricing. No per-request billing anxiety.
            </p>

            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {['Search', 'Sandboxes', 'Email', 'Storage', 'Inference'].map((service) => (
                <span key={service} className="px-4 py-2 bg-secondary rounded-sm text-sm font-medium">
                  {service}
                </span>
              ))}
            </div>

            <ul className="space-y-3 text-sm max-w-md mx-auto text-left">
              {[
                'Unlimited fair use across all services',
                'One unified API',
                'Priority support',
                'No hard caps',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-3">
                  <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CTA */}
        <section id="cta" className="py-20 px-6 bg-secondary/50 border-t border-border">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
              Stop Wrestling With Infrastructure
            </h2>
            <p className="text-lg text-muted-foreground mb-2">
              Request to Join the Beta Today
            </p>
            <p className="text-sm text-red-600 font-medium mb-8">
              Limited spots remaining
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 px-4 py-3 rounded-sm bg-white border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  required
                  disabled={loading}
                  autoComplete="email"
                  spellCheck={false}
                />
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-primary text-white font-medium rounded-sm hover:bg-primary/90 transition-colors whitespace-nowrap"
                  disabled={loading}
                >
                  {loading ? 'Sending…' : submitted ? 'Check email' : 'Request Access'}
                </button>
              </div>
              
              <label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1"
                  disabled={loading}
                />
                <span>
                  I consent to receive beta emails.{' '}
                  <Link to="/privacy" className="text-primary hover:underline">Privacy</Link>
                </span>
              </label>
              
              {error && (
                <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-sm">{error}</p>
              )}
              
              {submitted && !error && (
                <p className="text-sm text-green-700 bg-green-50 px-4 py-2 rounded-sm">
                  Thanks! Check your email.
                </p>
              )}
            </form>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-6 border-t border-border">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <span className="font-semibold">Twenty</span>
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} Twenty
              </p>
              <div className="flex items-center gap-6 text-sm">
                <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
                  Privacy
                </Link>
                <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                  Terms
                </Link>
                <Link to="/refund" className="text-muted-foreground hover:text-foreground transition-colors">
                  Refunds
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/refund" element={<RefundPolicy />} />
      <Route path="/waitlist" element={<Waitlist />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      
      {/* Protected Dashboard Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardOverview />} />
        <Route path="keys" element={<ApiKeysPage />} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
    </Routes>
  )
}

export default App
